import React, {FC} from "react";
import {Modal} from "antd";
import {InfoCircleOutlined} from "@ant-design/icons";
import moment from "moment";
import {renderAmount} from "../../../../helpers/renderAmount";

const css = require("./MolliePaymentSection.less");

// Sammel-Link-Modal (Sketch final-b-modal): Der Bucher bekommt EINE E-Mail mit
// EINEM Zahlungslink über die Summe aller offenen Mollie-Posten.
//
// Hinweistext folgt dem tatsächlichen Backend-Verhalten: Der Resolver ruft in
// `createBundleDispatchPaymentLink` `archiveExistingPaymentLinksForDispatch` für
// ALLE Bündel-Posten auf — die bestehenden Einzel-Zahlungslinks werden dabei
// entwertet und durch den Sammel-Link ersetzt.
export const BUNDLE_LINK_HINT =
    "Bestehende Einzel-Zahlungslinks dieser Posten werden ungültig und durch den Sammel-Link "
    + "ersetzt. Bezahlt der Kunde den Sammel-Link, werden alle enthaltenen Posten auf einmal "
    + "als bezahlt markiert.";

export const BUNDLE_LINK_TOOLTIP =
    "Der Kunde erhält eine E-Mail mit einem Zahlungslink über die Summe aller offenen Posten. "
    + "Bestehende Einzel-Links werden dabei ersetzt.";

export interface BundleLinkItem {
    id: string;
    title: string;
    paymentDue?: string | Date | null;
    amount: number;
}

export type BundleLinkModalProps = {
    visible: boolean;
    items: ReadonlyArray<BundleLinkItem>;
    totalAmount: number;
    bookerEmail?: string | null;
    loading: boolean;
    onCancel: () => void;
    onConfirm: () => void;
};

function formatDue(value: string | Date | null | undefined): string {
    if (!value) return "—";
    const parsed = moment(value);
    if (!parsed.isValid()) return "—";
    return parsed.format("L");
}

export const BundleLinkModal: FC<BundleLinkModalProps> = ({
    visible,
    items,
    totalAmount,
    bookerEmail,
    loading,
    onCancel,
    onConfirm,
}) => {
    return (
        /* AntD 4: `visible`, NICHT `open`. */
        <Modal
            title="Offene Posten gesammelt anfordern"
            visible={visible}
            onOk={onConfirm}
            onCancel={onCancel}
            okText={`Sammel-Link senden (${renderAmount({amount: totalAmount})})`}
            cancelText="Abbrechen"
            confirmLoading={loading}
            width={600}
            destroyOnClose
        >
            <div data-testid="mollie-bundle-modal-content">
                <p>
                    {"Der Kunde bekommt "}
                    <b>{"eine E-Mail mit einem Zahlungslink"}</b>
                    {" über die Summe aller offenen Posten. So muss er nicht jeden Posten einzeln bezahlen."}
                </p>
                <table className={css.bundleTable}>
                    <thead>
                        <tr>
                            <th>{"Fällig am"}</th>
                            <th>{"Posten"}</th>
                            <th className={css.bundleTableAmount}>{"Betrag"}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item) => (
                            <tr key={item.id} data-testid={`mollie-bundle-item-${item.id}`}>
                                <td>{formatDue(item.paymentDue)}</td>
                                <td>{item.title}</td>
                                <td className={css.bundleTableAmount}>
                                    {renderAmount({amount: item.amount})}
                                </td>
                            </tr>
                        ))}
                        <tr className={css.bundleTableSum}>
                            <td />
                            <td>{"Gesamtsumme"}</td>
                            <td
                                className={css.bundleTableAmount}
                                data-testid="mollie-bundle-total"
                            >
                                {renderAmount({amount: totalAmount})}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div className={css.bundleHint} data-testid="mollie-bundle-hint">
                    <InfoCircleOutlined className={css.bundleHintIcon} />
                    <span>{BUNDLE_LINK_HINT}</span>
                </div>
                {bookerEmail && (
                    <p className={css.bundleRecipient} data-testid="mollie-bundle-recipient">
                        {`Der Zahlungslink wird an ${bookerEmail} gesendet.`}
                    </p>
                )}
            </div>
        </Modal>
    );
};

export default BundleLinkModal;
